import { useCallback, useEffect } from 'react';

import { capitalize } from 'App/Helpers';
import { useDeferredReadyAction } from 'App/hooks';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import { sendPushGA } from '../helpers';
import { ONESIGNAL_PERMISSION_QUERY_ERROR } from '../OneSignal/constants';
import { useRequestPushPermission } from '../OneSignal/useRequestPushPermission';

/**
 * Handles in-game push subscription requests and syncs the result back to Unity.
 *
 * Addresses Unity through the `push.subscribe` and `push.status` slots. Pass
 * bridged `addEventListener` / `removeEventListener` / `sendMessage` from
 * `useUnityBridge` so each game contract resolves them to its own build names.
 *
 * Sends `push.status` to Unity only when `pushStatus` changes. `isLoaded` is
 * read inside the effect but left out of its deps on purpose: the status at
 * first load already travels with the init config, so flipping `isLoaded` to
 * true must not re-send it.
 *
 * If Unity asks to subscribe before the SDK is ready, the prompt is deferred
 * until `pushServiceReady` and then fired once.
 *
 * @param {Object} params
 * @param {Function} params.sendGameDesignEvent - Deferred GameAnalytics design event.
 * @param {Function} params.sendGoogleBasicEvent - Deferred Google Analytics basic event.
 * @param {string} params.pushStatus - Current notification permission status.
 * @param {boolean} params.pushServiceReady - Whether the push SDK has finished init.
 * @param {boolean} params.isLoaded - Whether the Unity/WebGL content has finished loading.
 * @param {(slotId: string, parameter?: *) => void} params.sendMessage - Bridged Unity sendMessage.
 * @param {import('react').RefObject<HTMLCanvasElement>} params.gameCanvasRef - Game canvas, refocused after the native prompt closes.
 * @param {(slotId: string, handler: Function) => void} params.addEventListener - Bridged Unity addEventListener.
 * @param {(slotId: string, handler: Function) => void} params.removeEventListener - Bridged Unity removeEventListener.
 * @param {boolean} [params.skipPromptIfAlreadyResolved=false] - Desktop app version only.
 *   Unity can ask to subscribe again after the user already granted or denied.
 *   When true, skip the native prompt and re-send the known status. Web and
 *   mobile omit this so they still call `requestPermission`.
 */
export const useGamePushSubscription = ({
    sendGameDesignEvent,
    sendGoogleBasicEvent,
    isLoaded,
    pushStatus,
    sendMessage,
    gameCanvasRef,
    pushServiceReady,
    addEventListener,
    removeEventListener,
    skipPromptIfAlreadyResolved = false,
}) => {
    const requestPermission = useRequestPushPermission();

    useEffect(() => {
        // The native permission prompt takes focus from the Unity canvas and
        // does not give it back when it closes. This listens for that change
        // and refocuses the canvas on the next tick.
        //
        // It only reads the current permission. It does not prompt. Safari and
        // iOS reject the query with "The operation is not supported", and push
        // is already unavailable there, so the lookup is skipped.
        if (pushStatus === 'notAvailable') return;
        if (typeof navigator.permissions?.query !== 'function') return;

        let alive = true;
        let permissionStatus = null;

        navigator.permissions
            .query({ name: 'notifications' })
            .then((status) => {
                if (!alive) return;

                permissionStatus = status;
                status.onchange = () => {
                    setTimeout(() => gameCanvasRef.current?.focus(), 0);
                };
            })
            .catch((error) => {
                captureCustomException(error, ONESIGNAL_PERMISSION_QUERY_ERROR);
            });

        return () => {
            alive = false;
            if (permissionStatus) permissionStatus.onchange = null;
        };
    }, [gameCanvasRef, pushStatus]);

    useEffect(() => {
        if (!isLoaded) return;

        sendMessage('push.status', capitalize(pushStatus));
        // `isLoaded` is intentionally omitted: the load-time value is already
        // in the init config, and including it would re-send on first load.
    }, [pushStatus, sendMessage]);

    const promptPushPermission = useCallback(
        async (gameScreenType) => {
            const permission = await requestPermission();

            if (!permission) return;

            sendPushGA({
                callSource: 'game',
                gameScreenType,
                permission,
                sendGameDesignEvent,
                sendGoogleBasicEvent,
            });
        },
        [sendGameDesignEvent, sendGoogleBasicEvent, requestPermission]
    );

    const runOrQueue = useDeferredReadyAction(
        pushServiceReady,
        promptPushPermission
    );

    const handlePushSubscription = useCallback(
        (gameScreenType) => {
            if (
                skipPromptIfAlreadyResolved &&
                (pushStatus === 'granted' || pushStatus === 'denied')
            ) {
                sendMessage('push.status', capitalize(pushStatus));
                return;
            }

            runOrQueue(gameScreenType);
        },
        [
            pushStatus,
            sendMessage,
            runOrQueue,
            skipPromptIfAlreadyResolved,
        ]
    );

    useEffect(() => {
        addEventListener('push.subscribe', handlePushSubscription);

        return () =>
            removeEventListener('push.subscribe', handlePushSubscription);
    }, [addEventListener, removeEventListener, handlePushSubscription]);
};
